/* ==========================================================================
   UNIT Solutions | Pro Hub - Reference Materials
   assets/prohub-reference-materials.js

   Vanilla JS, no dependencies. The cards are plain anchors, so the page works
   with JavaScript disabled. This file exists for three jobs:

   1. Restore rel="noopener noreferrer" on new-tab links, because Shopify's
      rich text sanitizer strips rel from schema defaults.
   2. Intercept same-page hash links in the capture phase. Motion's page
      transition script sets body.unloading on link clicks; a hash link that
      never navigates would otherwise leave the page faded to white.
   3. Warn in the theme editor when a document card has no URL yet.

   Initialisation waits for DOMContentLoaded so no query runs against a
   partially parsed document.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-prohub-rm]';
  var SCROLL_OFFSET = 120;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function closestFrom(node, selector) {
    var el = node && node.nodeType === 3 ? node.parentNode : node;
    if (!el || !el.closest) return null;
    return el.closest(selector);
  }

  function hardenLinks(root) {
    each(root.querySelectorAll('a[target="_blank"]'), function (link) {
      var rel = (link.getAttribute('rel') || '').toLowerCase();
      if (rel.indexOf('noopener') === -1 || rel.indexOf('noreferrer') === -1) {
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }

  function reportMissing(root) {
    if (!document.documentElement.classList.contains('shopify-design-mode') && !window.Shopify) return;
    var missing = root.querySelectorAll('[data-prohub-rm-missing]');
    if (!missing.length || !window.console || !window.console.warn) return;
    var titles = [];
    each(missing, function (card) {
      titles.push(card.getAttribute('data-prohub-rm-missing') || 'Untitled document');
    });
    window.console.warn('Pro Hub Reference Materials: no document URL set for - ' + titles.join(', '));
  }

  var anchorGuardBound = false;

  function bindAnchorGuard() {
    if (anchorGuardBound) return;
    anchorGuardBound = true;

    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = closestFrom(event.target, 'a[href]');
      if (!link || !link.closest(ROOT_SELECTOR)) return;

      var raw = link.getAttribute('href') || '';
      var hash = '';

      if (raw.charAt(0) === '#') {
        hash = raw.slice(1);
      } else {
        var url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin) return;
        if (url.pathname !== window.location.pathname) return;
        if (url.search !== window.location.search) return;
        if (!url.hash) return;
        hash = url.hash.slice(1);
      }

      if (!hash) return;

      var target = document.getElementById(hash);
      if (!target) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      document.body.classList.remove('unloading');

      var top = target.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop) - SCROLL_OFFSET;
      if (top < 0) top = 0;

      try {
        window.scrollTo({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      } catch (err) {
        window.scrollTo(0, top);
      }

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + hash);
      }

      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (err) { /* older browsers */ }
    }, true);
  }

  function initRoot(root) {
    if (!root || root.hasAttribute('data-prohub-rm-ready')) return;
    root.setAttribute('data-prohub-rm-ready', 'true');
    hardenLinks(root);
    reportMissing(root);
  }

  function boot() {
    bindAnchorGuard();
    each(document.querySelectorAll(ROOT_SELECTOR), initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target;
    if (!scope || !scope.querySelectorAll) return;
    bindAnchorGuard();
    each(scope.querySelectorAll(ROOT_SELECTOR), initRoot);
  });
})();
